import type { Icon } from "@phosphor-icons/react";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { MinusCircleIcon } from "@phosphor-icons/react/dist/csr/MinusCircle";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type {
  PayrollStatementApprovalHistoryEntry,
  PayrollStatementApprovalHistoryKind,
} from "./types";
import { formatNotificationTimestamp } from "./utils";

interface EntryStyle {
  icon: Icon;
  label: string;
}

/**
 * How to draw each act.
 *
 * `satisfies` rather than a bare `Record<string, EntryStyle>`, so a kind added to
 * `PayrollStatementApprovalHistoryKind` without a style here is a type error. Typing
 * it loosely made the runtime guard below cover for a missing KNOWN kind too, which
 * would have silently dropped it from the timeline — a guard against tomorrow's API
 * should not hide today's omission.
 */
const ENTRY_STYLES = {
  approved: { icon: CheckCircleIcon, label: "Approved" },
  withdrawn: { icon: MinusCircleIcon, label: "Withdrew approval" },
  pay_changed: { icon: WarningIcon, label: "Pay changed" },
} satisfies Record<PayrollStatementApprovalHistoryKind, EntryStyle>;

/**
 * How to draw one act, or nothing when this build has never heard of it.
 *
 * The kinds are the API's vocabulary, not ours. `approval_histories` is already
 * optional so a web deploy landing AHEAD of the API's degrades to no hover instead of
 * a crash; this is the same courtesy in the other direction. An API that grows a
 * fourth kind would otherwise have an older bundle render `<undefined />`, which
 * throws — and there is no error boundary between this and the packs table, so it
 * would take the whole row down rather than one tooltip.
 *
 * Takes a `string`, not the union, precisely because the value on the wire may not be
 * in the union this build was compiled against.
 */
const entryStyle = (kind: string): EntryStyle | undefined =>
  (ENTRY_STYLES as Record<string, EntryStyle>)[kind];

/** The acts this build can actually draw. Filtered before anything counts them. */
export const drawableApprovalHistory = (
  entries: PayrollStatementApprovalHistoryEntry[],
): PayrollStatementApprovalHistoryEntry[] =>
  entries.filter((entry) => entryStyle(entry.kind) !== undefined);

/**
 * When each act happened.
 *
 * A pay change has no timestamp of its own — nothing records the moment an edit moves
 * one technician's figures, so the API brackets it by the acts either side rather than
 * inventing an instant. The list is in order, so "after" plus the line below it is the
 * whole bracket; the one with nothing below is the outstanding one, and says so,
 * because that is the line a manager is about to act on.
 */
const entryDetail = (entry: PayrollStatementApprovalHistoryEntry) => {
  if (entry.kind !== "pay_changed") {
    return formatNotificationTimestamp(entry.at);
  }

  const after = formatNotificationTimestamp(entry.after);
  if (!after) return entry.before ? null : "Not approved since";

  return entry.before ? `After ${after}` : `After ${after}, not approved since`;
};

/**
 * A technician's approval, as a sequence of acts rather than a state.
 *
 * The column can only ever show the latest one, which is why a manager could not tell
 * an approval given once from one given, lost to an edit, and given again — or find
 * out that the tick they remember was withdrawn. Oldest first, the way the ledger
 * reads and the way somebody reconstructing what happened wants it.
 *
 * Rendered inside a tooltip, so it inherits that surface's foreground rather than
 * carrying its own text colours.
 *
 * Height-capped and scrollable. The endpoint returns the WHOLE ledger, and a technician
 * on a workbook that gets edited repeatedly can accumulate acts without limit — an
 * uncapped tooltip grows past the viewport and takes the oldest acts off the top of the
 * screen, where a hover cannot follow them. The cap is in `rem` against the tooltip's
 * own type scale rather than `vh`, so it holds the same number of acts on a laptop as
 * on a monitor, and the list scrolls in its own container so the page never does.
 */
export const ApprovalHistory = ({
  entries,
}: {
  entries: PayrollStatementApprovalHistoryEntry[];
}) => (
  <Stack align="start" gap={2} className="max-h-72 overflow-y-auto py-1 pr-1">
    <Text
      variant="caption"
      tone="inverse"
      className="font-medium tracking-widest uppercase opacity-60"
    >
      Approval history
    </Text>
    {entries.map((entry, index) => {
      const style = entryStyle(entry.kind);
      if (!style) return null;

      const Icon = style.icon;
      const detail = entryDetail(entry);

      return (
        <Stack
          // Acts repeat — approve, edit, approve again — so the kind is not a key. The
          // ledger is append-only and ordered, which makes the position a stable one.
          key={`${entry.kind}-${index}`}
          direction="row"
          align="start"
          gap={2}
        >
          <Icon className="mt-0.5 size-3.5 shrink-0" />
          <Stack align="start" gap={0}>
            <Text weight="semibold" variant="caption" tone="inverse">
              {style.label}
            </Text>
            {detail && (
              <Text variant="caption" tone="inverse" className="opacity-70">
                {detail}
              </Text>
            )}
          </Stack>
        </Stack>
      );
    })}
  </Stack>
);
